import { useCallback, useEffect, useState } from 'react';
import type {
  ActivityEntry,
  DashboardSummary,
  NeedsAttentionBucket,
  NeedsAttentionCategory,
} from '@mvcp/contracts';
import { getActivity, getDashboard } from '../api';
// The band vocabulary, pill, score number and Framework card have ONE owner
// (Story 8.1) — see `bands.tsx` for why it is web-local rather than in contracts.
import { BandPill, CoverageNote, FrameworkCard, ScoreValue } from './bands';
import type { DrillTarget } from './CockpitShell';
import { ProgressBar } from './ProgressBar';
import { Skeleton } from './Skeleton';

type DashState =
  | { kind: 'loading' }
  | { kind: 'ready'; summary: DashboardSummary; activity: ActivityEntry[] }
  | { kind: 'error'; message: string };

/** Short mono timestamp for the activity feed, e.g. `2026-07-19 09:00`. */
function shortTimestamp(iso: string): string {
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return iso;
  return new Date(t).toISOString().slice(0, 16).replace('T', ' ');
}

/**
 * Compliance Manager cockpit → Dashboard (Story 4.1, FR-16/FR-17). The epic's first
 * read surface over the Epic 3 engine: Sites & Vendors counts, the Overall Score
 * headline (mono `.kpi-number` + band pill + progress bar), one clickable card per
 * applicable Framework, the Needs-Attention buckets (authoritative counts), the
 * per-Site status list, and the 10 newest Recent Activity entries. Every value is
 * server-computed by consuming `@mvcp/domain`; this view only presents it.
 */
export function ComplianceDashboard({
  companyMaritime = false,
  onOpenFramework,
  onOpenRecord,
  onViewAllNeedsAttention,
  onExportReport,
  onCounts,
}: {
  companyMaritime?: boolean;
  /** Navigate to a Framework's detail view (Story 4.3 — supersedes the old peek placeholder). */
  onOpenFramework?: (frameworkId: string) => void;
  /**
   * Drill to the specific record behind a rollup (Story 4.4). A Needs-Attention item
   * opens its Vendor/Device; a Site-status row opens its Site. Supersedes the 4.1
   * Drawer-peek placeholder ("Drill-down … arrives in Story 4.4").
   */
  onOpenRecord?: (target: DrillTarget) => void;
  /**
   * Open the FULL Needs-Attention surface (Story 4.2, FR-19). The section-level
   * "View all →" calls it with no arg (all categories); a tile header calls it with
   * its own category. No-op safe when undefined (the tiles just show their preview).
   */
  onViewAllNeedsAttention?: (category?: NeedsAttentionCategory) => void;
  /** Open the shared report scope-picker for the whole Company (Story 6.1). */
  onExportReport?: () => void;
  /**
   * Report the summary's Sites / Vendors totals to the shell, which renders them as the
   * sidebar's inline count badges (DESIGN.md nav-item).
   *
   * A callback rather than a second fetch in the shell: `GET /dashboard` already returns
   * both numbers and this view already calls it on mount, so the badges cost nothing.
   * MUST be referentially stable (a `useState` setter or a `useCallback`) — it is an effect
   * dependency below, and an inline arrow would re-fire the effect on every render.
   */
  onCounts?: (counts: { sites: number; vendors: number }) => void;
}): React.JSX.Element {
  const [state, setState] = useState<DashState>({ kind: 'loading' });

  const load = useCallback(async () => {
    setState({ kind: 'loading' });
    try {
      // Fetch both endpoints together; either rejecting surfaces the error state
      // (never a partial/empty state masquerading as success).
      const [summary, activity] = await Promise.all([getDashboard(), getActivity()]);
      setState({ kind: 'ready', summary, activity });
    } catch (err: unknown) {
      setState({ kind: 'error', message: err instanceof Error ? err.message : 'Failed to load' });
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // Publish the counts on every successful load, including a Retry after an error — so the
  // sidebar badges recover with the view instead of staying stuck on the last good numbers.
  // Kept OUT of `load` so the fetch is not coupled to a caller-supplied callback: `load` is a
  // `useCallback` with an empty dep list, and adding `onCounts` to it would refetch the whole
  // dashboard whenever an unstable parent handed us a new function.
  useEffect(() => {
    if (state.kind !== 'ready') return;
    onCounts?.({ sites: state.summary.sitesCount, vendors: state.summary.vendorsCount });
  }, [state, onCounts]);

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <h1 style={{ margin: 0, fontSize: 23, fontWeight: 800, letterSpacing: '-0.4px' }}>
          Compliance Dashboard
        </h1>
        {companyMaritime && (
          <span className="status-pill" data-tone="neutral" title="Maritime fleet — maritime Frameworks apply">
            <span className="status-pill__dot" aria-hidden="true" />
            Maritime
          </span>
        )}
        {onExportReport && (
          <button
            type="button"
            className="btn-accent"
            data-surface="accent"
            onClick={onExportReport}
            style={{ marginLeft: 'auto' }}
          >
            Export Report
          </button>
        )}
      </div>

      {state.kind === 'loading' && <LoadingSkeleton />}

      {state.kind === 'error' && (
        <section className="card stack" role="alert">
          <h2 style={{ margin: 0, fontSize: 16, fontWeight: 800 }}>Couldn’t load the dashboard</h2>
          <p className="muted" style={{ margin: 0 }}>{state.message}</p>
          <button type="button" className="btn-accent" data-surface="accent" onClick={() => void load()} style={{ alignSelf: 'flex-start' }}>
            Retry
          </button>
        </section>
      )}

      {state.kind === 'ready' && (
        <ReadyDashboard
          summary={state.summary}
          activity={state.activity}
          onOpenFramework={onOpenFramework}
          onOpenRecord={onOpenRecord}
          onViewAllNeedsAttention={onViewAllNeedsAttention}
        />
      )}
    </>
  );
}

function LoadingSkeleton(): React.JSX.Element {
  return (
    <section className="card stack" aria-busy="true" aria-label="Loading dashboard">
      <Skeleton height={20} width="40%" />
      <Skeleton height={48} width="60%" />
      <Skeleton height={10} />
      <Skeleton height={90} />
      <Skeleton height={90} />
    </section>
  );
}

function ReadyDashboard({
  summary,
  activity,
  onOpenFramework,
  onOpenRecord,
  onViewAllNeedsAttention,
}: {
  summary: DashboardSummary;
  activity: ActivityEntry[];
  onOpenFramework?: (frameworkId: string) => void;
  onOpenRecord?: (target: DrillTarget) => void;
  onViewAllNeedsAttention?: (category?: NeedsAttentionCategory) => void;
}): React.JSX.Element {
  return (
    <>
      {/* Headline: counts + Overall Score */}
      <section className="card stack" aria-labelledby="overview-heading">
        <h2 id="overview-heading" style={{ margin: 0, fontSize: 16, fontWeight: 800 }}>Fleet overview</h2>
        <div style={{ display: 'flex', gap: 28, flexWrap: 'wrap' }}>
          <div>
            <div className="muted" style={{ fontSize: 13 }}>Sites</div>
            <div className="kpi-number" data-numeric>{summary.sitesCount}</div>
          </div>
          <div>
            <div className="muted" style={{ fontSize: 13 }}>Vendors</div>
            <div className="kpi-number" data-numeric>{summary.vendorsCount}</div>
          </div>
          <div style={{ minWidth: 220, flex: 1 }}>
            <div className="muted" style={{ fontSize: 13 }}>Overall score</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <ScoreValue score={summary.overall} name="Overall score" />
              <BandPill band={summary.overall.band} />
              {/* A percentage never travels without its coverage (AD-22): the headline
                  may read `Action Needed` at 90% precisely because part of the fleet's
                  in-scope suppliers/devices carry no evidence at all, and this note is
                  what makes that pair legible. Not announced — `ScoreValue`'s accessible
                  name above already speaks it, and saying it twice in one region is the
                  double-announcement `ProgressBar` is kept out of for the same reason. */}
              <CoverageNote score={summary.overall} />
            </div>
            <div style={{ marginTop: 8 }}>
              <ProgressBar percentage={summary.overall.percentage} band={summary.overall.band} />
            </div>
          </div>
        </div>
      </section>

      {/* Framework cards */}
      <section className="card stack" aria-labelledby="frameworks-heading">
        <h2 id="frameworks-heading" style={{ margin: 0, fontSize: 16, fontWeight: 800 }}>Frameworks</h2>
        {summary.frameworks.length === 0 ? (
          <p className="muted" style={{ margin: 0 }}>No frameworks apply to your organization yet.</p>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 12 }}>
            {summary.frameworks.map((f) => (
              <FrameworkCard
                key={f.frameworkId}
                code={f.code}
                name={f.name}
                score={f.score}
                onOpen={() => onOpenFramework?.(f.frameworkId)}
              />
            ))}
          </div>
        )}
      </section>

      {/* Needs Attention */}
      <section className="card stack" aria-labelledby="attention-heading">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <h2 id="attention-heading" style={{ margin: 0, fontSize: 16, fontWeight: 800 }}>Needs attention</h2>
          {/* "View all →" opens the FULL surface (every item, not the 5-item preview). */}
          {onViewAllNeedsAttention && (
            <button
              type="button"
              className="icon-button"
              onClick={() => onViewAllNeedsAttention()}
            >
              View all →
            </button>
          )}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 12 }}>
          <AttentionTile title="Expiring soon" category="expiring-soon" tone="warn" bucket={summary.needsAttention.expiringSoon} onOpenRecord={onOpenRecord} onViewAllNeedsAttention={onViewAllNeedsAttention} />
          <AttentionTile title="Expired / missing" category="expired-missing" tone="bad" bucket={summary.needsAttention.expiredMissing} onOpenRecord={onOpenRecord} onViewAllNeedsAttention={onViewAllNeedsAttention} />
          <AttentionTile title="Unverified vendors" category="unverified" tone="bad" bucket={summary.needsAttention.unverifiedVendors} onOpenRecord={onOpenRecord} onViewAllNeedsAttention={onViewAllNeedsAttention} />
        </div>
      </section>

      {/* Per-Site status */}
      <section className="card stack" aria-labelledby="sites-heading">
        <h2 id="sites-heading" style={{ margin: 0, fontSize: 16, fontWeight: 800 }}>Site status</h2>
        {summary.sites.length === 0 ? (
          <p className="muted" style={{ margin: 0 }}>No sites yet.</p>
        ) : (
          <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
            {summary.sites.map((s) => (
              <li key={s.siteId} style={{ borderTop: '1px solid var(--border)', paddingTop: 8 }}>
                {/* One action from a Site-status row opens that Site's detail (Story 4.4).
                    A real <button> so it is reachable + keyboard-operable with the global
                    focus ring. */}
                <button
                  type="button"
                  onClick={() => onOpenRecord?.({ kind: 'site', id: s.siteId })}
                  aria-label={`Open ${s.name}${s.worstDriver ? ` — ${s.worstDriver}` : ''}`}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    flexWrap: 'wrap',
                    width: '100%',
                    background: 'none',
                    border: 'none',
                    padding: 0,
                    font: 'inherit',
                    color: 'var(--text)',
                    textAlign: 'left',
                    cursor: 'pointer',
                  }}
                >
                  <span style={{ fontWeight: 700, minWidth: 140 }}>{s.name}</span>
                  <BandPill band={s.band} />
                  {s.worstDriver && <span className="muted" style={{ fontSize: 13 }}>{s.worstDriver}</span>}
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* Recent Activity */}
      <section className="card stack" aria-labelledby="activity-heading">
        <h2 id="activity-heading" style={{ margin: 0, fontSize: 16, fontWeight: 800 }}>Recent activity</h2>
        {activity.length === 0 ? (
          <p className="muted" style={{ margin: 0 }}>No recent activity yet.</p>
        ) : (
          <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
            {activity.map((a) => (
              <li key={a.id} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', borderTop: '1px solid var(--border)', paddingTop: 8 }}>
                <span>
                  <span style={{ fontWeight: 600 }}>{a.summary}</span>{' '}
                  <span className="muted" style={{ fontSize: 13 }}>by {a.actor}</span>
                </span>
                <span className="muted" style={{ fontFamily: 'var(--font-mono)', fontSize: 13 }} data-numeric>
                  {shortTimestamp(a.createdAt)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>
    </>
  );
}

function AttentionTile({
  title,
  category,
  tone,
  bucket,
  onOpenRecord,
  onViewAllNeedsAttention,
}: {
  title: string;
  category: NeedsAttentionCategory;
  tone: 'warn' | 'bad';
  bucket: NeedsAttentionBucket;
  onOpenRecord?: (target: DrillTarget) => void;
  onViewAllNeedsAttention?: (category?: NeedsAttentionCategory) => void;
}): React.JSX.Element {
  return (
    <div className="attention-tile stack" data-tone={tone} style={{ gap: 8 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
        {/* The tile header opens the FULL surface pre-filtered to this category. A real
            <button> (keyboard-operable, global focus ring); the item "Review" controls
            below are siblings, never nested inside it. No-op safe when the prop is absent. */}
        {onViewAllNeedsAttention ? (
          <button
            type="button"
            onClick={() => onViewAllNeedsAttention(category)}
            aria-label={`View all ${title.toLowerCase()} — ${bucket.count}`}
            style={{
              background: 'none',
              border: 'none',
              padding: 0,
              font: 'inherit',
              fontWeight: 700,
              fontSize: 14,
              /* The tile owns the foreground now (tone fg on a tone fill), so the header
                 inherits it rather than re-asserting the global text colour. */
              color: 'inherit',
              textAlign: 'left',
              cursor: 'pointer',
            }}
          >
            {title}
          </button>
        ) : (
          <span style={{ fontWeight: 700, fontSize: 14 }}>{title}</span>
        )}
        {/* Authoritative count — the bucket's true size, NOT the preview length. */}
        <span className="kpi-number" data-numeric data-tone={tone} style={{ fontSize: 22 }}>
          {bucket.count}
        </span>
      </div>
      {bucket.count === 0 ? (
        <p className="muted" style={{ margin: 0, fontSize: 13 }}>Nothing here — all clear.</p>
      ) : (
        <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
          {bucket.preview.map((item) => (
            <li key={item.id} style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'center' }}>
              <span style={{ fontSize: 13 }}>
                <span style={{ fontWeight: 600 }}>{item.label}</span>{' '}
                <span className="muted">{item.detail}</span>
              </span>
              {/* One action reaches the exact record: the item's subject (Vendor/Device)
                  detail, where the named Framework's certificate is listed (Story 4.4). */}
              <button
                type="button"
                className="btn-inline"
                onClick={() => onOpenRecord?.({ kind: item.subjectType, id: item.subjectId })}
                aria-label={`Review ${item.label} — opens the record`}
              >
                Review
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
